import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  ReactNode,
} from "react";
import { getLanguageDirection } from "./funcs";

const defaultLanguageData = require("./lang/en.json");

type LanguageData = {
  [key: string]: any;
};
type LanguageContextType = {
  languageData: LanguageData;
  setLanguageData: (data: LanguageData) => void;
  setLanguageCode?: (code: string) => Promise<void>;
};

const LanguageContext = createContext<LanguageContextType | undefined>(
  undefined
);

export function LanguageProvider({ children }: { children: ReactNode }) {
  const urlParams = useMemo(() => new URLSearchParams(window.location.search), []);

  const getInitialLang = () => {
    const qp = (urlParams.get("lang") || "").toLowerCase();
    if (qp) return qp;
    try {
      const saved = localStorage.getItem("lang") || "";
      if (saved) return saved.toLowerCase();
    } catch (e) {}
    return "en";
  };

  const initialLangCode = getInitialLang();

  // Try to hydrate initial language data synchronously to avoid initial English render
  const getInitialLanguageData = (): LanguageData | null => {
    if (initialLangCode === "en") {
      return defaultLanguageData.en;
    }
    try {
      const cached = localStorage.getItem(`LANGUAGE_DATA_${initialLangCode}`);
      if (cached) return JSON.parse(cached);
    } catch (e) {}
    return null; // Will async-load below; avoid rendering until loaded
  };

  const [languageData, setLanguageDataState] = useState<LanguageData | null>(
    getInitialLanguageData()
  );
  const [isLoaded, setIsLoaded] = useState<boolean>(!!getInitialLanguageData());

  useEffect(() => {
    let cancelled = false;
    if (isLoaded) return; // Already hydrated from cache or EN

    // Load selected non-EN language before first meaningful render
    import(`/src/lib/lang/${initialLangCode}.json`)
      .then((mod) => {
        if (cancelled) return;
        const data = mod.default[initialLangCode];
        setLanguageDataState(data);
        try {
          localStorage.setItem(
            `LANGUAGE_DATA_${initialLangCode}`,
            JSON.stringify(data)
          );
        } catch (e) {}
        setIsLoaded(true);
      })
      .catch(() => {
        // Fallback to EN if something goes wrong
        if (cancelled) return;
        setLanguageDataState(defaultLanguageData.en);
        setIsLoaded(true);
      });

    return () => {
      cancelled = true;
    };
  }, [initialLangCode, isLoaded]);

  // Keep document direction in sync
  useEffect(() => {
    if (!languageData) return;
    document.body.dir = getLanguageDirection(languageData);
  }, [languageData]);

  const setLanguageCode = async (code: string) => {
    const lang = (code || "en").toLowerCase();
    if (lang === "en") {
      setLanguageDataState(defaultLanguageData.en);
      try {
        localStorage.setItem(
          "LANGUAGE_DATA_en",
          JSON.stringify(defaultLanguageData.en)
        );
      } catch (e) {}
      return;
    }

    // Try cached first for instant UI switch
    try {
      const cached = localStorage.getItem(`LANGUAGE_DATA_${lang}`);
      if (cached) {
        setLanguageDataState(JSON.parse(cached));
      }
    } catch (e) {}

    try {
      const mod = await import(`/src/lib/lang/${lang}.json`);
      const data = mod.default[lang];
      setLanguageDataState(data);
      try {
        localStorage.setItem(`LANGUAGE_DATA_${lang}`, JSON.stringify(data));
      } catch (e) {}
    } catch (e) {
      // Keep current language if load fails
      // Optionally fallback to EN
    }
  };

  if (!isLoaded || !languageData) return null; // Block initial English flash

  return (
    <LanguageContext.Provider value={{
      languageData: languageData as LanguageData,
      setLanguageData: setLanguageDataState,
      setLanguageCode,
    }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
}
